import React, { useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Home, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Hero.css';

const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const titleWords = 'A fresh start starts here.'.split(' ');

  return (
    <section className="hero" ref={containerRef}>
      <motion.div className="hero-background" style={{ y }}>
        <div className="hero-image-overlay" />
        <img
          src="https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc4NTI5MTYyN3ww&ixlib=rb-4.1.0&q=85"
          alt="Community Support"
          className="hero-image"
        />
      </motion.div>

      <motion.div className="hero-content" style={{ opacity }}>
        <motion.div className="hero-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <span className="badge-dot" />
          Building better futures, one step at a time
        </motion.div>

        <h1 className="hero-title">
          {titleWords.map((word, i) => (
            <motion.span
              key={i}
              className="hero-title-word"
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.4 + i * 0.1,
                ease: [0.215, 0.610, 0.355, 1.000]
              }}
            >
              {word}{' '}
            </motion.span>
          ))}
        </h1>

        <motion.p
          className="hero-description"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          New Level Transition Homes provides safe, affordable, structured housing
          to help individuals rebuild their lives and regain independence.
        </motion.p>

        <motion.div
          className="hero-buttons"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <Link to="/apply">
            <motion.button
              className="btn btn-primary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Apply Now
              <ArrowRight size={20} />
            </motion.button>
          </Link>
          <motion.button
            className="btn btn-secondary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              document.getElementById('mission')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Learn More
          </motion.button>
        </motion.div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.6 }}
        >
          <div className="stat-item">
            <Home className="stat-icon" />
            <div className="stat-content">
              <div className="stat-number">100+</div>
              <div className="stat-label">Lives Changed</div>
            </div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <Users className="stat-icon" />
            <div className="stat-content">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support Available</div>
            </div>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <TrendingUp className="stat-icon" />
            <div className="stat-content">
              <div className="stat-number">95%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
      >
        <motion.div
          className="scroll-line"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
